import React from 'react';
import { Link } from 'react-router-dom';
import { Layers, ArrowLeft, Mail, HelpCircle, Bug, Lightbulb } from 'lucide-react';
import { Footer } from '../components/Footer';
export function ContactPage() {
  return <div className="min-h-screen bg-[#F8FAFC] flex flex-col font-sans text-[#0F172A]" data-id="element-28">
      {/* Simple Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50" data-id="element-29">
        <div className="container mx-auto px-6 max-w-6xl h-20 flex items-center justify-between" data-id="element-30">
          <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold text-[#0F172A] no-underline" data-id="element-31">
            <div className="w-8 h-8 bg-gradient-to-br from-[#0F172A] to-[#1E3A5F] rounded-md flex items-center justify-center text-white" data-id="element-32">
              <Layers size={16} data-id="element-33" />
            </div>
            CorkShip
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm font-medium text-[#64748B] hover:text-[#0284C7] transition-colors" data-id="element-34">
            <ArrowLeft size={16} data-id="element-35" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow py-16 px-6" data-id="element-36">
        <div className="max-w-2xl mx-auto" data-id="element-37">
          {/* Page Header */}
          <header className="mb-12 text-center" data-id="element-38">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0F172A] mb-4" data-id="element-39">
              Get in Touch
            </h1>
            <p className="text-[#64748B] text-lg" data-id="element-40">
              Have a question about CorkShip? We're here to help.
            </p>
          </header>

          {/* Main Contact Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 mb-8" data-id="element-41">
            <div className="flex items-start gap-4 mb-6" data-id="element-42">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#0284C7]/10 to-[#7DD3FC]/10 flex items-center justify-center flex-shrink-0" data-id="element-43">
                <Mail className="w-6 h-6 text-[#0284C7]" data-id="element-44" />
              </div>
              <div className="flex-grow" data-id="element-45">
                <h2 className="font-heading text-2xl font-bold text-[#0F172A] mb-2" data-id="element-46">
                  Email Support
                </h2>
                <a href="mailto:support@corkship.io" className="text-[#0284C7] hover:text-[#0F172A] text-lg font-medium transition-colors inline-block mb-3" data-id="element-47">
                  support@corkship.io
                </a>
                <p className="text-[#64748B] text-sm leading-relaxed" data-id="element-48">
                  We typically respond within 24 hours on business days.
                </p>
              </div>
            </div>
          </div>

          {/* Common Questions Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 mb-8" data-id="element-49">
            <div className="flex items-center gap-3 mb-6" data-id="element-50">
              <HelpCircle className="w-6 h-6 text-[#0284C7]" data-id="element-51" />
              <h2 className="font-heading text-2xl font-bold text-[#0F172A]" data-id="element-52">
                Common Questions
              </h2>
            </div>

            <div className="space-y-6" data-id="element-53">
              {/* Question 1 */}
              <div className="border-b border-slate-100 pb-6 last:border-0 last:pb-0" data-id="element-54">
                <h3 className="font-bold text-lg text-[#0F172A] mb-2" data-id="element-55">
                  Setup & Installation
                </h3>
                <p className="text-[#64748B] leading-relaxed" data-id="element-56">
                  Install CorkShip from the Shopify App Store or Commerce7 App
                  Marketplace, then connect your Australia Post eParcel account
                  in the settings. The setup wizard will guide you through
                  configuring your parcel sizes and shipping preferences.
                </p>
              </div>

              {/* Question 2 */}
              <div className="border-b border-slate-100 pb-6 last:border-0 last:pb-0" data-id="element-57">
                <h3 className="font-bold text-lg text-[#0F172A] mb-2" data-id="element-58">
                  Australia Post Account
                </h3>
                <p className="text-[#64748B] leading-relaxed" data-id="element-59">
                  You'll need an active Australia Post eParcel business account.
                  If you don't have one, visit{' '}
                  <a href="https://auspost.com.au/business/shipping/eparcel" target="_blank" rel="noopener noreferrer" className="text-[#0284C7] hover:underline" data-id="element-60">
                    auspost.com.au/business
                  </a>{' '}
                  to sign up. You'll need your Account Number, API Key, and API
                  Secret.
                </p>
              </div>

              {/* Question 3 */}
              <div className="border-b border-slate-100 pb-6 last:border-0 last:pb-0" data-id="element-61">
                <h3 className="font-bold text-lg text-[#0F172A] mb-2" data-id="element-62">
                  Billing
                </h3>
                <p className="text-[#64748B] leading-relaxed" data-id="element-63">
                  CorkShip costs $14.99 USD per month with a 14-day free trial.
                  Billing is handled through your platform's billing system. You
                  can cancel anytime from your store admin.
                </p>
              </div>

              {/* Question 4 */}
              <div className="border-b border-slate-100 pb-6 last:border-0 last:pb-0" data-id="element-64">
                <h3 className="font-bold text-lg text-[#0F172A] mb-2" data-id="element-65">
                  Technical Issues
                </h3>
                <p className="text-[#64748B] leading-relaxed" data-id="element-66">
                  If you're experiencing technical issues, first check that your
                  Australia Post credentials are correct and your account is
                  active. For persistent problems, email us with details about
                  the error and we'll investigate promptly.
                </p>
              </div>
            </div>
          </div>

          {/* Additional Contact Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-67">
            {/* Report a Bug */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6" data-id="element-68">
              <div className="flex items-start gap-3 mb-3" data-id="element-69">
                <Bug className="w-5 h-5 text-[#0284C7] flex-shrink-0 mt-0.5" data-id="element-70" />
                <div data-id="element-71">
                  <h3 className="font-bold text-[#0F172A] mb-1" data-id="element-72">
                    Report a Bug
                  </h3>
                  <p className="text-sm text-[#64748B] leading-relaxed mb-2" data-id="element-73">
                    Found something broken? Let us know so we can fix it.
                  </p>
                  <a href="mailto:support@corkship.io" className="text-sm text-[#0284C7] hover:underline font-medium" data-id="element-74">
                    support@corkship.io
                  </a>
                </div>
              </div>
            </div>

            {/* Feature Requests */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6" data-id="element-75">
              <div className="flex items-start gap-3 mb-3" data-id="element-76">
                <Lightbulb className="w-5 h-5 text-[#FCD34D] flex-shrink-0 mt-0.5" data-id="element-77" />
                <div data-id="element-78">
                  <h3 className="font-bold text-[#0F172A] mb-1" data-id="element-79">
                    Feature Requests
                  </h3>
                  <p className="text-sm text-[#64748B] leading-relaxed mb-2" data-id="element-80">
                    Have an idea to make CorkShip better? We'd love to hear it.
                  </p>
                  <a href="mailto:support@corkship.io" className="text-sm text-[#0284C7] hover:underline font-medium" data-id="element-81">
                    support@corkship.io
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer data-id="element-82" />
    </div>;
}